import { useEffect, useRef, useState } from "react";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { AlertCircle, Radio, Trash2 } from "lucide-react";
import { AdminSideNav } from "./AdminDashboardPage";
import BrandedHeader from "@/components/BrandedHeader";

const MAX_EVENTS = 200;

// Derive WebSocket URL from REACT_APP_BACKEND_URL (http→ws, https→wss).
function wsUrl(path) {
  const base = process.env.REACT_APP_BACKEND_URL || window.location.origin;
  return base.replace(/^http/, "ws") + path;
}

function severityColor(sev) {
  switch ((sev || "").toLowerCase()) {
    case "critical":
      return "bg-red-600 text-white";
    case "high":
      return "bg-orange-500 text-white";
    case "medium":
      return "bg-yellow-400 text-black";
    default:
      return "bg-gray-200 text-gray-800";
  }
}

export default function AdminProctorLivePage() {
  const [events, setEvents] = useState([]);
  const [status, setStatus] = useState("connecting"); // connecting | connected | disconnected
  const [lastPing, setLastPing] = useState(null);
  const wsRef = useRef(null);
  const reconnectTimer = useRef(null);

  useEffect(() => {
    let cancelled = false;

    const connect = () => {
      // Bearer token is the fallback mechanism in preview; cookies handle prod.
      const bearer = sessionStorage.getItem("sec_bearer") || "";
      const url = wsUrl(`/api/ws/proctor-violations${bearer ? `?token=${encodeURIComponent(bearer)}` : ""}`);
      const ws = new WebSocket(url);
      wsRef.current = ws;

      ws.onopen = () => {
        if (cancelled) return;
        setStatus("connected");
      };
      ws.onmessage = (e) => {
        if (cancelled) return;
        try {
          const msg = JSON.parse(e.data);
          if (msg.type === "ping") {
            setLastPing(new Date().toISOString());
            return;
          }
          if (msg.type === "violation" && msg.data) {
            setEvents((prev) => [
              { ...msg.data, _receivedAt: new Date().toISOString() },
              ...prev,
            ].slice(0, MAX_EVENTS));
          }
        } catch (err) {
          console.warn("ProctorLive: malformed WS frame", err);
        }
      };
      ws.onclose = () => {
        if (cancelled) return;
        setStatus("disconnected");
        // exponential-ish backoff, capped at 10s
        reconnectTimer.current = setTimeout(connect, 3000);
      };
      ws.onerror = (err) => {
        console.warn("ProctorLive: WS error", err);
        try { ws.close(); } catch (closeErr) { console.warn("ProctorLive: close failed", closeErr); }
      };
    };

    connect();
    return () => {
      cancelled = true;
      if (reconnectTimer.current) clearTimeout(reconnectTimer.current);
      if (wsRef.current) wsRef.current.close();
    };
  }, []);

  const clearFeed = () => setEvents([]);

  return (
    <div className="min-h-screen bg-background" data-testid="proctor-live-page">
      <BrandedHeader variant="admin" />
      <div className="flex">
        <AdminSideNav />
        <div className="flex-1 p-6 max-w-6xl mx-auto">
          <div className="flex items-center justify-between mb-6">
            <div>
              <h1 className="text-3xl font-bold flex items-center gap-3">
                <Radio className={`w-7 h-7 ${status === "connected" ? "text-green-600 animate-pulse" : "text-gray-400"}`} />
                Live Proctor Violations
              </h1>
              <p className="text-gray-600 mt-1">
                Real-time stream of candidate proctoring events across all active sessions.
              </p>
            </div>
            <div className="flex items-center gap-3">
              <Badge
                variant={status === "connected" ? "default" : "outline"}
                className={status === "connected" ? "bg-green-600" : ""}
                data-testid="ws-status-badge"
              >
                {status}
              </Badge>
              <Button
                variant="outline"
                size="sm"
                onClick={clearFeed}
                disabled={events.length === 0}
                data-testid="clear-feed-btn"
              >
                <Trash2 className="w-4 h-4 mr-1.5" /> Clear
              </Button>
            </div>
          </div>

          <Card>
            <CardHeader>
              <CardTitle className="text-base">
                {events.length} event{events.length === 1 ? "" : "s"} · newest first
              </CardTitle>
              <CardDescription>
                Kept in memory — max {MAX_EVENTS}. Refreshing the page resets the feed.
                {lastPing && <span className="ml-2 text-xs">· last heartbeat {new Date(lastPing).toLocaleTimeString()}</span>}
              </CardDescription>
            </CardHeader>
            <CardContent>
              {events.length === 0 ? (
                <div className="py-16 text-center text-gray-500" data-testid="empty-feed">
                  <AlertCircle className="w-12 h-12 mx-auto mb-3 opacity-30" />
                  <p>Waiting for violations…</p>
                  <p className="text-xs mt-1">Candidates currently taking tests will surface here as they trigger policy events.</p>
                </div>
              ) : (
                <ul className="divide-y" data-testid="violations-feed">
                  {events.map((ev, i) => {
                    const v = ev.violation || {};
                    return (
                      <li key={`${v.id || i}`} className="py-3 flex items-start gap-3" data-testid="violation-row">
                        <Badge className={`${severityColor(v.severity)} shrink-0 uppercase`}>
                          {v.severity || "low"}
                        </Badge>
                        <div className="flex-1 min-w-0">
                          <div className="flex items-center gap-2 flex-wrap">
                            <span className="font-medium">{v.type || "unknown"}</span>
                            {ev.candidate_id && (
                              <span className="text-xs text-gray-500">
                                candidate <code>{String(ev.candidate_id).slice(0, 8)}</code>
                              </span>
                            )}
                            {ev.test_id && (
                              <span className="text-xs text-gray-500">
                                · test <code>{String(ev.test_id).slice(0, 8)}</code>
                              </span>
                            )}
                          </div>
                          {v.details && <p className="text-sm text-gray-700 mt-0.5">{v.details}</p>}
                          <p className="text-xs text-gray-500 mt-0.5">
                            {v.timestamp ? new Date(v.timestamp).toLocaleString() : ""}
                            {ev._receivedAt && (
                              <span className="ml-2">
                                · received {new Date(ev._receivedAt).toLocaleTimeString()}
                              </span>
                            )}
                          </p>
                        </div>
                      </li>
                    );
                  })}
                </ul>
              )}
            </CardContent>
          </Card>
        </div>
      </div>
    </div>
  );
}
